import { useEffect, useState } from "react";
import { LOGO_URL } from "@/components/Logo";

/**
 * Premium ink-bleed preloader.
 */
export const Preloader = ({ minDuration = 2200 }) => {
  const [phase, setPhase] = useState("loading");

  useEffect(() => {
    document.body.style.overflow = "hidden";

    const t1 = setTimeout(() => setPhase("leaving"), minDuration);
    const t2 = setTimeout(() => setPhase("gone"), minDuration + 800);

    // Safety net: always restore scroll even if cleanup doesn't fire
    const safetyNet = setTimeout(() => {
      document.body.style.overflow = "";
    }, minDuration + 1200);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(safetyNet);
      document.body.style.overflow = "";
    };
  }, [minDuration]);

  // Restore scroll immediately when phase transitions to gone
  useEffect(() => {
    if (phase === "gone") {
      document.body.style.overflow = "";
    }
  }, [phase]);

  if (phase === "gone") return null;

  return (
    <div
      data-testid="preloader"
      aria-hidden="true"
      className={`fixed inset-0 z-[100] flex items-center justify-center bg-[#0A0A0A] transition-opacity duration-[900ms] ease-[cubic-bezier(0.65,0,0.35,1)] ${
        phase === "leaving" ? "opacity-0 pointer-events-none" : "opacity-100"
      }`}
    >
      {/* Radial ink bloom background */}
      <div className="absolute inset-0 pointer-events-none">
        <svg
          viewBox="0 0 1200 800"
          className="w-full h-full opacity-50"
          preserveAspectRatio="xMidYMid slice"
        >
          <defs>
            <radialGradient id="inkGrad" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#E07020" stopOpacity="0.35" />
              <stop offset="40%" stopColor="#E07020" stopOpacity="0.06" />
              <stop offset="100%" stopColor="#E07020" stopOpacity="0" />
            </radialGradient>
            <filter id="inkBlur">
              <feGaussianBlur stdDeviation="40" />
            </filter>
          </defs>
          <circle cx="600" cy="400" r="380" fill="url(#inkGrad)" filter="url(#inkBlur)">
            <animate
              attributeName="r"
              from="120"
              to="440"
              dur="2s"
              fill="freeze"
              calcMode="spline"
              keySplines="0.16 1 0.3 1"
            />
          </circle>
        </svg>
      </div>

      <div className="absolute inset-0 grain opacity-60 pointer-events-none" />

      <div className="relative z-10 flex flex-col items-center px-6">
        <div className="relative w-[140px] h-[140px] md:w-[180px] md:h-[180px]">
          <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" aria-hidden="true">
            <defs>
              <filter id="goo">
                <feGaussianBlur stdDeviation="6" />
                <feColorMatrix values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 24 -12" />
              </filter>
            </defs>
            <g filter="url(#goo)" fill="#E07020">
              <circle cx="100" cy="105" r="0" className="ink-blob ink-blob-1" />
              <circle cx="60" cy="110" r="0" className="ink-blob ink-blob-2" />
              <circle cx="140" cy="115" r="0" className="ink-blob ink-blob-3" />
              <circle cx="80" cy="140" r="0" className="ink-blob ink-blob-4" />
              <circle cx="125" cy="150" r="0" className="ink-blob ink-blob-5" />
            </g>
          </svg>

          <div className="absolute inset-3 md:inset-4 bg-[#F5F0EB] flex items-center justify-center preloader-logo ring-2 ring-bendito-orange/40 shadow-[0_0_40px_rgba(224,112,32,0.35)]">
            <img src={LOGO_URL} alt="Bendito Tattoo logo" className="w-[78%] h-[78%] object-contain" />
          </div>
        </div>

        <div className="mt-7 text-center">
          <div className="font-heading text-bendito-ink text-3xl md:text-4xl tracking-[0.35em] preloader-wordmark">
            BENDITO
          </div>
          <div className="font-mono text-[10px] md:text-[11px] tracking-[0.45em] text-bendito-orange uppercase mt-3">
            Tattoo · Playa del Carmen
          </div>
        </div>

        <div className="mt-8 w-[160px] h-px bg-bendito-border overflow-hidden">
          <div className="h-full w-full bg-bendito-orange preloader-bar" />
        </div>
      </div>

      <style>{`
        @keyframes ink-bloom {
          0% { r: 0; opacity: 0; }
          60% { opacity: 1; }
          100% { opacity: 1; }
        }
        .ink-blob { animation: ink-bloom 1.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .ink-blob-1 { animation-delay: 0.05s; animation-name: ink-blob-1; }
        .ink-blob-2 { animation-delay: 0.18s; animation-name: ink-blob-2; }
        .ink-blob-3 { animation-delay: 0.26s; animation-name: ink-blob-3; }
        .ink-blob-4 { animation-delay: 0.42s; animation-name: ink-blob-4; }
        .ink-blob-5 { animation-delay: 0.58s; animation-name: ink-blob-5; }
        @keyframes ink-blob-1 { to { r: 60; } }
        @keyframes ink-blob-2 { to { r: 36; } }
        @keyframes ink-blob-3 { to { r: 30; } }
        @keyframes ink-blob-4 { to { r: 18; } }
        @keyframes ink-blob-5 { to { r: 22; } }

        @keyframes logo-reveal {
          0% { opacity: 0; transform: translateY(14px) scale(0.92); filter: blur(8px); }
          60% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .preloader-logo {
          opacity: 0;
          animation: logo-reveal 1.4s cubic-bezier(0.16, 1, 0.3, 1) 0.45s forwards;
        }

        @keyframes wordmark-reveal {
          0% { opacity: 0; letter-spacing: 0.6em; }
          100% { opacity: 1; letter-spacing: 0.35em; }
        }
        .preloader-wordmark {
          opacity: 0;
          animation: wordmark-reveal 1s ease-out 0.9s forwards;
        }

        @keyframes loader-bar {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(0); }
        }
        .preloader-bar {
          transform: translateX(-100%);
          animation: loader-bar 1.8s cubic-bezier(0.65, 0, 0.35, 1) 0.6s forwards;
        }
      `}</style>
    </div>
  );
};

export default Preloader;
